<template>
  <g>
    <!-- Small star staggered pattern - covers mat area with opening cut out -->
    <g v-for="star in stars" :key="star.key">
      <!-- Diamond/star shape -->
      <path :d="starPath(star.x, star.y)" :fill="colorHex"/>
      <!-- Surrounding circles: 2 pairs vertically + 1 on each side = 6 total -->
      <circle :cx="star.x" :cy="star.y + circleOffsetVerticalNear" :r="circleRadius" :fill="colorHex"/>
      <circle :cx="star.x" :cy="star.y + circleOffsetVerticalFar" :r="circleRadius" :fill="colorHex"/>
      <circle :cx="star.x" :cy="star.y - circleOffsetVerticalNear" :r="circleRadius" :fill="colorHex"/>
      <circle :cx="star.x" :cy="star.y - circleOffsetVerticalFar" :r="circleRadius" :fill="colorHex"/>
      <circle :cx="star.x + circleOffsetHorizontal" :cy="star.y" :r="circleRadius" :fill="colorHex"/>
      <circle :cx="star.x - circleOffsetHorizontal" :cy="star.y" :r="circleRadius" :fill="colorHex"/>
    </g>
  </g>
</template>

<script>
// Distance between adjacent star centers within the same row, in inches. Odd-numbered rows are
// shifted right by half this value, creating the staggered/brick pattern.
const HORIZONTAL_PITCH = 2;
// Distance between star centers in adjacent rows, in inches.
const VERTICAL_PITCH = 1.625;
// Shifts the entire pattern left/right after centering on the mat, in inches. Positive moves right.
const HORIZONTAL_OFFSET = 0;
// Shifts the entire pattern up/down after centering on the mat, in inches. Positive moves down.
const VERTICAL_OFFSET = 0;

// Half-width (and half-height) of the 4-pointed diamond shape, in inches. The diamond's four
// points are exactly this distance from the center in each cardinal direction.
const STAR_DIAMOND_SIZE = 0.109375;
// Radius of each of the 6 small circles surrounding the diamond, in inches.
const CIRCLE_RADIUS = 0.02;
// Distance from the star center to the inner pair of circles above and below the diamond, in inches.
// There are two circles on each vertical side — this is the closer one to the diamond.
const CIRCLE_OFFSET_VERTICAL_NEAR = 0.15625;
// Distance from the star center to the outer pair of circles above and below the diamond, in inches.
// There are two circles on each vertical side — this is the farther one from the diamond.
const CIRCLE_OFFSET_VERTICAL_FAR = 0.21;
// Distance from the star center to each of the two side circles (left and right), in inches.
const CIRCLE_OFFSET_HORIZONTAL = 0.15625;

export default {
  props: {
    artworkOpenings: {
      type: Array,
      required: true
    },
    matWidth: {
      type: Number,
      required: true
    },
    matHeight: {
      type: Number,
      required: true
    },
    decorationColor: {
      type: String,
      default: "black"
    }
  },
  data() {
    return {
      circleRadius: CIRCLE_RADIUS,
      circleOffsetVerticalNear: CIRCLE_OFFSET_VERTICAL_NEAR,
      circleOffsetVerticalFar: CIRCLE_OFFSET_VERTICAL_FAR,
      circleOffsetHorizontal: CIRCLE_OFFSET_HORIZONTAL
    };
  },
  computed: {
    colorHex() {
      return this.decorationColor === 'white' ? '#ffffff' : '#050708';
    },
    stars() {
      const stars = [];
      const matW = this.matWidth;
      const matH = this.matHeight;

      // Calculate how many stars we need to cover the mat
      const cols = Math.ceil(matW / HORIZONTAL_PITCH) + 2;
      const rows = Math.ceil(matH / VERTICAL_PITCH) + 2;

      // Calculate starting position to center the pattern on the mat
      const totalPatternWidth = (cols - 1) * HORIZONTAL_PITCH;
      const totalPatternHeight = (rows - 1) * VERTICAL_PITCH;
      const startX = (matW - totalPatternWidth) / 2 + HORIZONTAL_OFFSET;
      const startY = (matH - totalPatternHeight) / 2 + VERTICAL_OFFSET;

      // Get the opening bounds (use first opening for now)
      const opening = this.artworkOpenings[0];
      if (!opening) return stars;

      // Skip stars whose center falls within the inner opening — the mat mask handles clipping
      // partial stars at the bevel edge.
      const openingLeft = opening.x;
      const openingTop = opening.y;
      const openingRight = opening.x + opening.width;
      const openingBottom = opening.y + opening.height;

      for (let row = 0; row < rows; row++) {
        const isOffsetRow = row % 2 === 1;
        const rowStartX = startX + (isOffsetRow ? HORIZONTAL_PITCH / 2 : 0);
        const y = startY + row * VERTICAL_PITCH;

        for (let col = 0; col < cols; col++) {
          const x = rowStartX + col * HORIZONTAL_PITCH;

          // Skip stars that fall within the opening area
          if (x > openingLeft && x < openingRight && y > openingTop && y < openingBottom) {
            continue;
          }

          // Skip stars that are outside the mat bounds (with small margin for partial stars)
          if (x < -0.1 || x > matW + 0.1 || y < -0.1 || y > matH + 0.1) {
            continue;
          }

          stars.push({
            key: `star-${row}-${col}`,
            x,
            y
          });
        }
      }

      return stars;
    }
  },
  methods: {
    starPath(cx, cy) {
      // Create a 4-pointed star/diamond shape centered at (cx, cy)
      // Based on original SVG: curved diamond with subtle bezier control points
      const s = STAR_DIAMOND_SIZE;
      // Control point offsets (from original: 3.6/7.4 ≈ 0.49, 1/7.4 ≈ 0.14)
      const cp1 = s * 0.49;
      const cp2 = s * 0.14;
      // Path starts at left point, goes clockwise: left → bottom → right → top → left
      return `M${cx - s},${cy} c${cp1},${cp2} ${s - cp2},${cp1} ${s},${s} c${cp2},-${cp1} ${cp1},-${s + cp2} ${s},-${s} c-${cp1},-${cp2} -${s + cp2},-${cp1} -${s},-${s} c-${cp2},${cp1} -${cp1},${s - cp2} -${s},${s}Z`;
    }
  }
};
</script>
